import React, { useEffect, useMemo, useState } from "react";
import { Page, PendingButton, LoadingMsg, FormGroup, SubmitButton } from "components/common";
import moment from "moment";
import { Label, Input, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Row, Col } from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { Table } from "reactstrap";
import { Link } from "react-router-dom";
import { useHistory } from 'react-router';
import { exportDownload } from "services/util";
import download from "downloadjs";
import { useXeroClient } from "hooks";
import { flushSync } from "react-dom";
import { useCustomModal } from "services/useBaseModal";
import ReportsSettings from "./ReportsSettings";
function TransProcessing(t, reportData) {
  // Translate ReportTitles array
  reportData.ReportTitles = reportData.ReportTitles.map(title => t(title));

  // Translate Summary cells
  reportData.Summary = reportData.Summary.map(row => ({
    ...row,
    Cells: row.Cells.map(cell => ({
      ...cell,
      Value: t(cell.Value),
    })),
  }));

  // Translate Table headers and rows
  reportData.Table.Header = reportData.Table.Header.map(header => ({
    ...header,
    Value: t(header.Value),
  }));

  reportData.Table.Rows = reportData.Table.Rows.map(row => ({
    ...row,
    Cells: row.Cells.map(cell => ({
      ...cell,
      Value: t(cell.Value),
    })),
  }));

  return reportData;
}

const ZatcaReport = ({ client, t, selectedOrganisation }) => {
  const [showDetails, setShowDetails] = useState(false)
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(false);
  const history = useHistory();
  const xero = useXeroClient();
  const { i18n } = useTranslation();
  const dir = i18n.dir();
  const currentLanguage = i18n.language;

  const [item, setItem] = useState({});
  const [exporting, setExporting] = useState(false)
  const [settings, setSettings] = useState(null);

  const [setSettingModal, SettingsToggle, SettingsModal] = useCustomModal();

  const [extraRow, setExtraRow] = useState({
    correction: 0.00,
    carried_vat: 0.00,
    current_total: 0.00,
    adjusted_total: 0.00,
  })

  const getReport = () => {
    setHistory()
    loadReport()
  }

  const loadReport = async () => {
    setPending(true);
    try {
      let resp = await client
        .get(`xero_actions/${selectedOrganisation.id}/zatca_tax_report`, {
          params: { ...item, lang: currentLanguage },
        })
        .get("data");
      setData(resp);
      let current_total = parseFloat(resp.Table.Rows[resp.Table.Rows.length - 1].Cells[3].Value || 0.00)
      setExtraRow({ ...extraRow, current_total, adjusted_total: current_total })
    } catch (ex) {
      if (ex.response && ex.response.data) {
        NotificationManager.error(t(ex.response.data));
      } else {
        NotificationManager.error(t("An unexpected error occurred"));
      }
    }
    setPending(false);
  };

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const fromDate = params.get('from')
    const toDate = params.get('to')
    setItem((old) => (
      {
        ...old, 'fromDate': fromDate ? fromDate : moment().startOf("month").format("YYYY-MM-DD"),
        'toDate': toDate ? toDate : moment().startOf("month").format("YYYY-MM-DD"),
      }
    ))
  }, []);

  useEffect(() => {
    setExtraRow({ ...extraRow, adjusted_total: (extraRow.current_total + (parseFloat(extraRow.carried_vat) || 0) + (parseFloat(extraRow.correction) || 0)).toFixed(2) })
  }, [extraRow.carried_vat, extraRow.correction])


  const setHistory = () => {
    history.push({
      pathname: '/reports/zatca',
      search: `?from=${item.fromDate}&to=${item.toDate}`
    })
  }

  const ReportExport = (title, format) => async () => {
    flushSync(() => {
      setExporting(true);
    });
    setPending(true);
    const filename = `${title}.${format}`;
    try {
      if (format === "pdf") {
        const payload = document.getElementById("report").outerHTML
        const { content } = await xero
          .post("all/generate_document", {
            template: `<div style="font-size: 20px">${payload}</div>`,
            filename,
          })
          .get("data");
        download(content, filename);
      } else if (format === "csv" || format === "xlsx") {
        data['Table']['Rows'].push({
          "RowType": "Row",
          "Cells": [{ "Value": "Corrections from previous periods (between ± 15,000 SAR)" }, { "Value": "" }, { "Value": "" }, { "Value": `${extraRow.correction}` }]
        })
        data['Table']['Rows'].push({
          "RowType": "Row",
          "Cells": [{ "Value": "VAT carried over from previous period/s" }, { "Value": "" }, { "Value": "" }, { "Value": `${extraRow.carried_vat}` }]
        })
        data['Table']['Rows'].push({
          "RowType": "SummaryRow",
          "Cells": [{ "Value": "Net Tax Due (or refunded)" }, { "Value": "" }, { "Value": "" }, { "Value": `${extraRow.adjusted_total}` }]
        })
        let _data = {
          "ReportTitles": data['ReportTitles'],
          "Table": data['Table'],
          "Summary": data['Summary'],
        }
        exportDownload(client, `xero_actions/${selectedOrganisation.id}/export_report`, filename, {}, { data: TransProcessing(t, _data) }, "POST")
      }
    } finally {
      setPending(false);
      setExporting(false)
    }
  }


  return (
    <Page title="Zatca Tax Return Report" className="inv-table">
      <div className="d-print-none inv-tabs ">
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center mt-3">
            <FormGroup className="mr-3">
              <Label>
                <Trans>From Date</Trans>
              </Label>
              <DatePicker
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                selected={item.fromDate ? moment(item.fromDate) : null}
                className="form-control"
                dateFormat="D MMM YYYY"
                onChange={(value) => {
                  const m = moment(value);
                  let newItem = { ...item };
                  newItem.fromDate = m.isValid ? m.format("YYYY-MM-DD") : null;
                  setItem(newItem);
                }}
              />
            </FormGroup>
            <FormGroup className="mr-3">
              <Label>
                <Trans>To Date</Trans>
              </Label>
              <DatePicker
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                selected={item.toDate ? moment(item.toDate) : null}
                className="form-control"
                dateFormat="D MMM YYYY"
                onChange={(value) => {
                  const m = moment(value);
                  let newItem = { ...item };
                  newItem.toDate = m.isValid ? m.format("YYYY-MM-DD") : null;
                  setItem(newItem);
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label>&nbsp;</Label>
              <PendingButton
                color="primary"
                className="d-block"
                onClick={getReport}
                pending={pending}
              >
                <Trans>Update</Trans>
              </PendingButton>
            </FormGroup>
            <FormGroup>
              <Label>&nbsp;</Label>
              <UncontrolledDropdown>
                <DropdownToggle disabled={data ? false : true} color="primary" caret>
                  <span className="ml-1">
                    {pending ? <span className="fa fa-spin fa-spinner mr-2" /> : ""}
                    <Trans>Export</Trans>
                  </span>
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={ReportExport("zatca_report", "xlsx")}>
                    <Trans>Excel</Trans>
                  </DropdownItem>
                  <DropdownItem onClick={ReportExport("zatca_report", "pdf")}>
                    <Trans>PDF</Trans>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </FormGroup>
          </div>
          <ReportsSettings setSettings={setSettings} settings={settings} isPartial={false}/>
        </div>
      </div>

      {pending ? (
        <LoadingMsg />
      ) : (
        <div>
          {data && data?.Table &&
            <div id="report" dir={dir}>
              <div className="text-left my-4">
                {data.ReportTitles && data.ReportTitles.map((i, idx) => (
                  <div key={idx} className="h5"><Trans>{i}</Trans></div>
                ))}
              </div>
              <div className="border my-3 p-3" >
                <Table responsive className="text-left" >
                  <thead>
                    <tr>
                      {data?.Table?.Header?.map((i, idx) => (
                        <th className="text-left h5 font-weight-bold" key={idx}>
                          <Trans>{i.Value}</Trans>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.Table?.Rows?.map((row, idx) => (
                      <tr key={idx}>
                        {row?.Cells?.map((cell, idy) => (
                          <td className={`text-left ${row.RowType == 'SummaryRow' ? 'font-weight-bold' : ''}`} key={`${idx}-${idy}`}>
                            <Trans>{cell.Value}</Trans>
                          </td>
                        ))
                        }
                      </tr>
                    ))}
                    <tr>
                      <td className={`text-left`}>
                        <Trans>Corrections from previous periods (between ± 15,000 SAR)</Trans>
                      </td>
                      <td></td>
                      <td></td>
                      <td>
                        {
                          exporting ?
                            <span >{extraRow.correction}</span>
                            :
                            <FormGroup className="mr-3">
                              <Input type="number" value={extraRow.correction} onChange={(e) => setExtraRow({ ...extraRow, correction: e.target.value })} />
                            </FormGroup>

                        }
                      </td>
                    </tr>
                    <tr>
                      <td className={`text-left`}>
                        <Trans>VAT carried over from previous period/s</Trans>
                      </td>
                      <td></td>
                      <td></td>
                      <td>
                        {
                          exporting ?
                            <span >{extraRow.carried_vat}</span>
                            :
                            <FormGroup className="mr-3">
                              <Input type="number" value={extraRow.carried_vat} onChange={(e) => setExtraRow({ ...extraRow, carried_vat: e.target.value })} />
                            </FormGroup>
                        }
                      </td>
                    </tr>
                    <tr>
                      <td className={`text-left font-weight-bold`}>
                        <Trans>Net Tax Due (or refunded)</Trans>
                      </td>
                      <td></td>
                      <td></td>
                      <td className="font-weight-bold text-left">
                        <span>{extraRow.adjusted_total}</span>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          }
          {data &&
            <div className="border my-3 text-center">
              <button className="btn btn-link link text-success shadow-none" onClick={(e) => { e.preventDefault(); setShowDetails(!showDetails) }}>
                {t(showDetails ? "Hide Details" : "Show Details")}
              </button>
            </div>
          }
          {showDetails && data && data?.DetailRows &&
            <div className="border my-3 p-3">
              <Table responsive className="text-left">
                <thead>
                  <tr>
                    {data?.DetailRows?.Header?.map((i) => (
                      <th className="text-left h6">
                        <Trans>{i.Value}</Trans>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.DetailRows?.Rows?.map((row, index) => (
                    <>
                      <tr>
                        {row?.Cells?.map((cell) => (
                          <td className={`text-left ${row.RowType === 'SummaryRow' ? 'font-weight-bold h4' : (row.RowType === 'TotalRow' && '')}`}>
                            {cell.Value?.Reference ?
                              <Link to={cell.Value.Url} className="text-success">
                                <Trans>{cell.Value.Reference}</Trans>
                              </Link>
                              :
                              <Trans>{cell.Value}</Trans>
                            }
                          </td>
                        ))
                        }
                      </tr>
                      {row.RowType === 'TotalRow' && data?.DetailRows?.Rows.length - 1 !== index &&
                        <tr><div className="py-2"></div></tr>
                      }
                    </>
                  ))}
                </tbody>
              </Table>
            </div>
          }
          {!data &&
            <p className="text-center my-5 h5"><Trans>Pick some dates and click on update!</Trans></p>
          }
        </div>
      )}

      
    </Page>
  );
};


export default ZatcaReport;
